/* eslint-disable @typescript-eslint/camelcase */
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { Component, Vue } from 'vue-property-decorator'
import AppForm from '@core/components/AppForm.vue'
import AppDialog from '@core/components/AppDialog.vue'
import AppDatatable from '@core/components/AppDatatable.vue'
import { IDateService } from '@master/services/IDateService'
import { FormStructure } from '@master/model/structure/FormStructure'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { PayeeFullMeta, PayeeFullMetaHeaders } from '@core/model/common/PayeeFullMeta'
import { PayeeFullMetaList } from '@core/model/common/PayeeFullMetaList'
import { camelCase } from 'lodash/fp'
import { downloadCSVFile, parseCsvFile } from '@core/services/CSVService'
import { CountryOptions } from '@master/model/options/NationalityAndCountryOptions'
import { IProjectInfo } from '@master/config/IProjectInfo'

@Component({
  components: {
    AppForm,
    AppDialog,
    AppDatatable
  }
})
export class PayeeMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  @inject()
  private projectInfo: IProjectInfo

  protected showPayeeModal = false
  protected txtSearch = ''
  protected mode: 'edit' | 'create' = 'create'

  protected payee: PayeeFullMeta = new PayeeFullMeta()
  protected existingPayeeOidList = []
  protected payeeList: PayeeFullMetaList = null
  protected showImportModal = false
  protected uploadedCSVTableData: PayeeFullMetaList = null
  protected txtSearchModal = ''
  protected uploadReady = true

  get companyName () {
    return `${this.projectInfo.companyShortName} Code` ?? 'Company Code'
  }

  get payeeStructure (): FormStructure<PayeeFullMeta> {
    return [
      {
        label: 'Full Name',
        key: 'payeeFullname',
        classes: ['colspan']
      },
      {
        label: 'NRIC/FIN/Passport/UEN No.',
        key: 'payeeNric'
      },
      {
        label: this.companyName,
        key: 'payeeCompanyCode'
      },
      {
        label: 'Contact',
        key: 'payeeContact'
      },
      {
        label: 'Email',
        key: 'payeeEmail'
      },
      {
        label: 'Date of Birth/ Incorporation',
        key: 'payeeDob',
        type: 'date'
      },
      {
        label: 'Place of Birth/ Incorporation',
        key: 'payeePob'
      },
      {
        label: 'Residential/ Registered Address',
        key: 'payeeAddress'
      },
      {
        label: 'Postal Code',
        key: 'payeePostalCode'
      },
      {
        label: 'Country',
        key: 'payeeCountry',
        options: CountryOptions
      },
      {
        label: 'Bank Name',
        key: 'payeeBankName',
        classes: ['colspan']
      },
      {
        label: 'Bank Account No',
        key: 'payeeBank'
      },
      {
        label: 'SWIFT Code',
        key: 'payeeSwiftCode'
      }
    ]
  }

  get columns (): {label: string; field: keyof PayeeFullMeta}[] {
    return [
      { label: 'Full Name', field: 'payeeFullname' },
      { label: 'NRIC', field: 'payeeNric' },
      { label: 'Email Address', field: 'payeeEmail' }
    ]
  }

  get list () {
    return !this.payeeList ? [] : this.payeeList.list.map(payeeMeta => {
      return {
        ...payeeMeta,
        emit: true
      }
    })
  }

  get newRecordsDataList () {
    return !this.uploadedCSVTableData ? [] : this.uploadedCSVTableData.list
  }

  get newRecordsColumn () {
    return [
      { label: 'oID', field: 'oid' },
      { label: 'Fullname', field: 'payeeFullname' },
      { label: 'Email', field: 'payeeEmail' },
      { label: 'NRIC/FIN/Passport/UEN No.', field: 'payeeNric' },
      { label: this.companyName, field: 'payeeCompanyCode' },
      { label: 'Contact', field: 'payeeContact' },
      { label: 'DOB/ Incorporation', field: 'payeeDob' },
      { label: 'POB/ Incorporation', field: 'payeePob' },
      { label: 'Residential/ Registered Address', field: 'payeeAddress' },
      { label: 'Postal Code', field: 'payeePostalCode' },
      { label: 'Country', field: 'payeeCountry' },
      { label: 'Bank Name', field: 'payeeBankName' },
      { label: 'Bank', field: 'payeeBank' },
      { label: 'Swift Code', field: 'payeeSwiftCode' }
    ]
  }

  addNewPayee () {
    this.payee = new PayeeFullMeta()
    this.mode = 'create'
    this.showPayeeModal = true
  }

  editPayee (payee: any) {
    this.payee = new PayeeFullMeta()
    this.mode = 'edit'
    Object.assign(this.payee, payee)
    // convert DOB from string to date
    this.payee.payeeDob = new Date(this.payee.payeeDob)
    this.showPayeeModal = true
  }

  async fetchPayeeList () {
    const res = await this.$api.payee.list({
      headers: JSON.stringify(PayeeFullMetaHeaders)
    })
    this.payeeList = new PayeeFullMetaList(res.data)
    this.existingPayeeOidList = this.payeeList.list.map(payee => payee.oid)
  }

  async upsertPayee () {
    await this.$api.payee.update({
      _oid: this.mode === 'create' ? 'new' : this.payee.oid,
      meta: this.payee.convertToRawData()
    })
    this.fetchPayeeList()
    await this.uiService.alert.show({
      type: 'success',
      message: 'Saved successfully!'
    })
    this.showPayeeModal = false
  }

  openModalImport () {
    this.showImportModal = true
    this.uploadedCSVTableData = null

    // Reset input file
    this.uploadReady = false
    this.$nextTick(() => {
      this.uploadReady = true
    })
  }

  mapRawKeytoFormatKey (list: string[]) {
    return list.map(key => {
      return camelCase(key)
    })
  }

  exportCSV () {
    const column = this.mapRawKeytoFormatKey(PayeeFullMetaHeaders)
    downloadCSVFile(column, this.list, 'payee-list')
  }

  processCSVFile (file, callBack) {
    if (file) {
      parseCsvFile(file[0], callBack)
    }
  }

  setUploadedCSVTableData (result) {
    this.uploadedCSVTableData = new PayeeFullMetaList(result)
  }

  onCSVFileSelected (evt) {
    const file = evt.target.files
    if (file) {
      this.processCSVFile(file, this.setUploadedCSVTableData)
    }
  }

  async savePayee (payee, isCreate) {
    await this.$api.payee.update({
      _oid: isCreate ? 'new' : payee.oid,
      meta: payee.convertToRawData()
    })
  }

  async importCSV () {
    if (this.newRecordsDataList.length === 0 || !this.newRecordsDataList) {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'No file selected'
      })
      return false
    }

    this.newRecordsDataList.forEach((newPayee) => {
      if (this.existingPayeeOidList.includes(newPayee.oid)) {
        this.savePayee(newPayee, false)
      } else {
        this.savePayee(newPayee, true)
      }
    })
    this.showImportModal = false
    await this.uiService.alert.show({
      type: 'success',
      message: 'CSV Imported successfully!'
    })
    await this.fetchPayeeList()
  }

  async mounted () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService
    await this.fetchPayeeList()
  }
}
